import type { ComponentType } from 'react';
import type { JSONSchema, SchemaFormWidgets } from 'formhell';

type SchemaNode = Record<string, any>;

const isNode = (value: unknown): value is SchemaNode =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const escapeToken = (token: string) => token.replace(/~/g, '~0').replace(/\//g, '~1');

const resolveLocalRef = (node: SchemaNode, root: SchemaNode, seen: Set<string>): SchemaNode | undefined => {
    const ref = node.$ref;
    if (typeof ref !== 'string' || !ref.startsWith('#/')) {
        return node;
    }
    if (seen.has(ref)) {
        return undefined; // recursive schema: FormHell can't expand it forever either
    }
    seen.add(ref);
    const target = ref.slice(2).split('/')
        .map(token => token.replace(/~1/g, '/').replace(/~0/g, '~'))
        .reduce<unknown>((acc, token) => (isNode(acc) ? acc[token] : undefined), root);
    if (!isNode(target)) {
        return node;
    }
    const { $ref, ...rest } = node;
    return resolveLocalRef({ ...target, ...rest }, root, seen);
};


export const collectWidgetPointers = (
    schema: JSONSchema,
    resolveWidget: (node: SchemaNode, pointer: string) => ComponentType<any> | undefined
): SchemaFormWidgets => {
    const root = schema as SchemaNode;
    const found: SchemaFormWidgets = {};

    const walk = (rawNode: SchemaNode, pointer: string, seen: Set<string>) => {
        const node = resolveLocalRef(rawNode, root, seen);
        if (!node) {
            return;
        }
        const widget = resolveWidget(node, pointer);
        if (widget) {
            found[pointer] = widget;
            return; // the widget owns everything below it
        }
        if (isNode(node.properties)) {
            Object.entries(node.properties).forEach(([key, child]) => {
                if (isNode(child)) walk(child, `${pointer}/properties/${escapeToken(key)}`, new Set(seen));
            });
        }
        if (Array.isArray(node.prefixItems)) {
            node.prefixItems.forEach((child: unknown, index: number) => {
                if (isNode(child)) walk(child, `${pointer}/prefixItems/${index}`, new Set(seen));
            });
        }
        if (isNode(node.items)) {
            walk(node.items, `${pointer}/items`, new Set(seen));
        }
    };

    walk(root, '', new Set());
    return found;
};
